import { DEFAULT_COLOR_ID } from '../model/colors.ts';
import { DEFAULT_LEGEND, labelFor, labelForExact } from '../model/lineColors.ts';
import type { LegendLabel, LightStyle } from '../model/types.ts';

/**
 * Settings defaults and the pure parts of the original `state/settings.ts` (zustand store
 * actions as functions over the data, and the v1 → v2 persist migration with its
 * AsyncStorage side effect returned instead of performed).
 */

export const DEFAULT_STYLE: LightStyle = {
  layout: 'single',
  spacingIn: 12,
  pairGapIn: 4,
  size: 1.25,
  brightness: 1,
  washFt: 6,
};

/** v1 stored pricing tiers; their colors become line colors. */
export interface LegacyTier {
  id: string;
  name: string;
  color: string;
  product: string;
  pricePerFt: number | null;
}

export const SETTINGS_KEY = 'better-lights.settings.v1';
export const LEGACY_TIERS_KEY = 'better-lights.legacyTiers';
export const SETTINGS_VERSION = 2;

/**
 * Who may place and drag points. 'auto' switches to Pencil-only the first time an Apple
 * Pencil / stylus touches the canvas (fingers then pan and zoom); 'any' lets fingers draw too.
 */
export type PencilMode = 'auto' | 'pencil' | 'any';

export interface AiPrefs {
  quality: 'medium' | 'high';
  windowGlow: boolean;
}

/** Persisted settings (the `state` of `better-lights.settings.v1`, version 2). */
export interface SettingsData {
  legendLabels: Record<string, LegendLabel>;
  rememberLegend: boolean;
  legendEnabled: boolean;
  customLineColors: string[];
  currency: string;
  defaultStyle: LightStyle;
  defaultColorId: string;
  customHex: string;
  recentColorIds: string[];
  ai: AiPrefs;
  pencilMode: PencilMode;
  freehandSnap: boolean;
  aiConsentAt: number | null;
}

/**
 * Currency symbols offered in Settings (the price list prefix: "$28/ft"). The stored value is the
 * symbol itself, so quotes and synced settings are unchanged; mirrored in the iOS and Android apps.
 */
export const CURRENCY_OPTIONS: readonly { symbol: string; label: string }[] = [
  { symbol: '$', label: 'Dollar (US, Canada, Australia, NZ)' },
  { symbol: '€', label: 'Euro' },
  { symbol: '£', label: 'British pound' },
  { symbol: '¥', label: 'Japanese yen' },
  { symbol: '₹', label: 'Indian rupee' },
  { symbol: 'kr', label: 'Krona / krone (SE, NO, DK)' },
  { symbol: 'CHF', label: 'Swiss franc' },
  { symbol: 'R$', label: 'Brazilian real' },
  { symbol: 'R', label: 'South African rand' },
  { symbol: 'zł', label: 'Polish złoty' },
  { symbol: '₱', label: 'Philippine peso' },
];

/** The currency choices for a picker: the list, plus the current symbol when it isn't one of them (typed in an older version). */
export function currencyChoices(current: string): { symbol: string; label: string }[] {
  return CURRENCY_OPTIONS.some((c) => c.symbol === current) || !current
    ? [...CURRENCY_OPTIONS]
    : [...CURRENCY_OPTIONS, { symbol: current, label: 'Your symbol' }];
}

export const DEFAULT_SETTINGS: SettingsData = {
  legendLabels: DEFAULT_LEGEND,
  rememberLegend: true,
  legendEnabled: true,
  customLineColors: [],
  currency: '$',
  defaultStyle: DEFAULT_STYLE,
  defaultColorId: DEFAULT_COLOR_ID,
  customHex: '#FF5A1F',
  recentColorIds: [],
  ai: { quality: 'high', windowGlow: true },
  pencilMode: 'auto',
  freehandSnap: true,
  aiConsentAt: null,
};

/* ------------------------------------------------------------------ pure actions */

/** Merges `patch` onto the label for `hex` (case-insensitive lookup, fixed 07 §B9). */
export function setSettingsLegendLabel(
  legendLabels: Record<string, LegendLabel>,
  hex: string,
  patch: Partial<LegendLabel>,
): Record<string, LegendLabel> {
  return { ...legendLabels, [hex]: { ...labelFor(legendLabels, hex), ...patch } };
}

/** ORIGINAL settings merge (exact-string lookup). */
export function setSettingsLegendLabelOriginal(
  legendLabels: Record<string, LegendLabel>,
  hex: string,
  patch: Partial<LegendLabel>,
): Record<string, LegendLabel> {
  return { ...legendLabels, [hex]: { ...labelForExact(legendLabels, hex), ...patch } };
}

/** Dedupes and keeps the last 8. */
export function addCustomLineColor(customLineColors: string[], hex: string): string[] {
  return customLineColors.includes(hex) ? customLineColors : [...customLineColors, hex].slice(-8);
}

export function removeCustomLineColor(customLineColors: string[], hex: string): string[] {
  return customLineColors.filter((c) => c !== hex);
}

/** Most recent first, max 6. */
export function rememberColor(recentColorIds: string[], id: string): string[] {
  return [id, ...recentColorIds.filter((c) => c !== id)].slice(0, 6);
}

/* -------------------------------------------------------------------- migrations */

export interface SettingsMigration {
  state: Record<string, unknown>;
  /** Side effect the original performed: write `better-lights.legacyTiers` = JSON of `[[id, color], ...]`. */
  writeLegacyTiers: [string, string][] | null;
}

/** Persist migration of `better-lights.settings.v1` from `version` to 2 (input is not mutated). */
export function migrateSettings(persisted: unknown, version: number): SettingsMigration {
  const old = { ...((persisted ?? {}) as Record<string, unknown>) };
  let writeLegacyTiers: [string, string][] | null = null;
  if (version < 2) {
    // v1 kept pricing tiers; each becomes the label of its line color.
    const tiers = (old.tiers as LegacyTier[] | undefined) ?? [];
    const legendLabels: Record<string, LegendLabel> = tiers.length ? {} : DEFAULT_LEGEND;
    for (const t of tiers) legendLabels[t.color] = { name: t.name, product: t.product, pricePerFt: t.pricePerFt };
    // Keep tierId → color for v1 projects, which may rehydrate after this rewrite.
    writeLegacyTiers = tiers.map((t) => [t.id, t.color]);
    delete old.tiers;
    old.legendLabels = legendLabels;
    // Old defaults rendered LEDs too small; move untouched defaults to the new ones.
    const style = old.defaultStyle as LightStyle | undefined;
    if (style && style.size === 1 && style.pairGapIn === 3) old.defaultStyle = { ...style, size: DEFAULT_STYLE.size, pairGapIn: DEFAULT_STYLE.pairGapIn };
  }
  return { state: old, writeLegacyTiers };
}

/**
 * tierId → line color, from v1 settings as stored (for migrating v1 projects). Pure version of
 * the original `legacyTierColors()`: pass the raw strings stored under `better-lights.legacyTiers`
 * and `better-lights.settings.v1` (or null). Unreadable JSON falls back like the original:
 * entries parsed before the failure are kept.
 */
export function legacyTierColors(savedLegacyTiers: string | null, rawSettings: string | null): Map<string, string> {
  const map = new Map<string, string>([
    ['tier-signature', '#22D3EE'],
    ['tier-pairs', '#F5A524'],
    ['tier-accent', '#E879F9'],
  ]);
  try {
    for (const [id, color] of savedLegacyTiers ? (JSON.parse(savedLegacyTiers) as [string, string][]) : []) map.set(id, color);
    const tiers = rawSettings ? ((JSON.parse(rawSettings).state?.tiers ?? []) as LegacyTier[]) : [];
    for (const t of tiers) map.set(t.id, t.color);
  } catch {
    // Unreadable settings: fall back to the default tier colors.
  }
  return map;
}

/** Saved price list for the project v2 → v3 migration (`savedPriceList()` in the original). */
export function savedPriceList(rawSettings: string | null, inMemory: Record<string, LegendLabel>): Record<string, LegendLabel> {
  try {
    const state = rawSettings ? JSON.parse(rawSettings).state : null;
    if (state?.legendLabels) return state.legendLabels;
  } catch {
    // Fall through to the in-memory list.
  }
  return { ...inMemory };
}
